import { useCallback, useEffect, useRef, useState } from 'react';

/** How close to the bottom (px) still counts as "reading the latest". */
export const NEAR_BOTTOM_PX = 80;

/**
 * ChatGPT-style scroll pinning for a scroll container:
 * - while the reader is near the bottom, new content keeps it at the bottom;
 * - once they scroll up, nothing yanks them back — `showJump` turns on instead;
 * - `scrollToBottom()` (the "Jump to latest" pill, or a send) goes back down.
 * Growth of the content AND of the box (the composer shrinking/growing) is
 * observed with one ResizeObserver.
 */
export function useStickToBottom<S extends HTMLElement, C extends HTMLElement>() {
  const scrollRef = useRef<S | null>(null);
  const contentRef = useRef<C | null>(null);
  const pinned = useRef(true);
  const [showJump, setShowJump] = useState(false);

  // Instant on purpose: a smooth scroll keeps heading for the height it started
  // with, so while text streams in it lands short and reads as "scrolled up".
  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    pinned.current = true;
    setShowJump(false);
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let sizes = { scroll: el.scrollHeight, client: el.clientHeight };
    const onScroll = () => {
      // A scroll caused by layout (the box or the content changed size — the
      // browser clamps scrollTop, and scroll events fire before ResizeObserver)
      // is not the reader's: never let it unpin; re-stick if pinned.
      const layoutScroll = el.scrollHeight !== sizes.scroll || el.clientHeight !== sizes.client;
      sizes = { scroll: el.scrollHeight, client: el.clientHeight };
      if (layoutScroll && pinned.current) {
        el.scrollTop = el.scrollHeight;
        return;
      }
      const near = el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
      pinned.current = near;
      setShowJump(!near);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const content = contentRef.current;
    const el = scrollRef.current;
    if (!content || !el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (pinned.current) el.scrollTop = el.scrollHeight;
      else setShowJump(el.scrollHeight - el.scrollTop - el.clientHeight > NEAR_BOTTOM_PX);
    });
    ro.observe(content);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return { scrollRef, contentRef, showJump, scrollToBottom };
}
